import {Component, ElementRef, EventEmitter, HostBinding, Input, OnInit, Output} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {FeatureTypeService} from '@catcore/services';
import {EntitySchemaService} from '@catcore/services/entity-schema.service';
import {Feature} from '@catcore/services/feature/feature';
import {ResourceList} from '@catcore/types/base';
import {extractDataFromModel} from '@catcore/utils';
import {FeaturePortConnectionService} from '@catmodules/models/feature-port/services/feature-port-connection.service';
import {FeaturePortService} from '@catmodules/models/feature-port/services/feature-port.service';
import {FeaturePort, FeaturePortConnection} from '@catmodules/models/feature-port/types';
import {PortService} from '@catmodules/models/ports/services/port.service';
import {Port} from '@catmodules/models/ports/types';
import {ActiveModal} from '@catmodules/widgets/slide-in-modal/utils/modal-ref';
import {StyleType} from '@catui/types';
import {SelectItem} from 'primeng/primeng';
import {Observable} from 'rxjs/Observable';
import {isNullOrUndefined} from 'util';

const componentBaseName = 'modal-create-port-connection';
const selectorName = 'cat-' + componentBaseName;
const defaultClassNames = [selectorName].join(' ');

/**
 * The component provides the functionality to create a new FeaturePortConnectionCreateInfo for
 * a port and returns the new created to the opener component
 */
@Component({
  templateUrl: './create-port-connection.component.html',
  styleUrls: ['./create-port-connection.component.scss'],
})
export class CreatePortConnectionComponent implements OnInit {
  @HostBinding('class') classNames = defaultClassNames;
  @Output() afterCreate = new EventEmitter<FeaturePortConnection>();

  @Input() title: string;

  @Input()
  get source(): FeaturePort {
    return this._source;
  }

  set source(value: FeaturePort) {
    this._source = value;
    if (!isNullOrUndefined(value)) {
      this.form.patchValue({port1: value});
      this.refreshSelectOptionsForFeaturePorts(this.featurePorts);
    }
  }

  get readyToSubmit() {
    let isReady = !isNullOrUndefined(this.form);
    isReady = isReady && !isNullOrUndefined(this.source);
    isReady = isReady && this.form.dirty && this.form.valid;
    return isReady;
  }

  get canCreate() {
    if (this._source) {
      return this._source.output || !this.featurePortService.hasIncomingPortConnection(this._source);
    } else {
      return true;
    }
  }

  form: FormGroup;

  labels = {
    targetPort: 'Target Port',
    timeout: 'Timeout',
    speed: 'Speed',
    no_advertise_speed: 'No Advertise Speed',
  };
  controlRefs = {
    targetPort: 'targetPort',
    timeout: 'timeout',
    speed: 'speed',
    isVirtual: 'isVirtual',
    no_advertise_speed: 'no_advertise_speed',
  };

  styleType = StyleType;
  portOptions: SelectItem[] = [];

  private featurePorts: FeaturePort[] = [];
  private ports: Port[] = [];
  private _source: FeaturePort;

  constructor(public el: ElementRef,
              private schemaService: EntitySchemaService,
              private formBuilder: FormBuilder,
              private featureService: FeatureTypeService,
              private portService: PortService,
              private featurePortService: FeaturePortService,
              private featurePortConnectionService: FeaturePortConnectionService,
              private activeModal: ActiveModal) {
    this.form = this.formBuilder.group({
      targetPort: this.formBuilder.control('', [Validators.required]),
      timeout: this.formBuilder.control('', []),
      speed: this.formBuilder.control('', []),
      isVirtual: this.formBuilder.control(false, []),
      no_advertise_speed: this.formBuilder.control('', []),
    });
  }

  ngOnInit() {
    this.featureService
      .getCurrent()
      .mergeMap((feature: Feature) => {
        const featurePortService = this.featureService.getPorts(feature);
        featurePortService.load();
        return featurePortService.getCurrent();
      })
      .subscribe((ports: ResourceList) => {
        if (!isNullOrUndefined(ports)) {
          this.featurePorts = ports._embedded.item;
          this.refreshSelectOptionsForFeaturePorts(this.featurePorts);
        }
      });
    this.portService
      .getAll()
      .map((ports: Port[]) => {
        if (isNullOrUndefined(ports)) {
          return [];
        }
        return ports;
      })
      .subscribe((ports: Port[]) => this.ports = ports);

  }

  createItem() {
    let subscribeable;
    if (this.form.value.isVirtual) {
      subscribeable = this.createVirtualPortConnection(this.source, this.form);
    } else {
      subscribeable = this.createRealPortConnection(this.source, this.form);
    }
    subscribeable.subscribe((portConnection: FeaturePortConnection) => {
      this.afterCreate.emit(portConnection);
      this.activeModal.close(portConnection);
    });
  }

  updatePortOptions($event) {
    if (this.form.value.isVirtual) {
      this.refreshSelectOptionsForPorts(this.ports);
    } else {
      this.refreshSelectOptionsForFeaturePorts(this.featurePorts);
    }
    this.form.patchValue({targetPort: null});
  }

  private createRealPortConnection(featurePort: FeaturePort, form: FormGroup): Observable<any> {
    return this.createPortConnection(featurePort, form.value.targetPort, form.value.speed, form.value.no_advertise_speed);
  }

  private createVirtualPortConnection(featurePort: FeaturePort, form: FormGroup): Observable<any> {
    return this.featurePortService
      .createVirtualPort(form.value.targetPort)
      .mergeMap((port: FeaturePort) => this.createPortConnection(featurePort, port, form.value.speed, form.value.no_advertise_speed));
  }

  private createPortConnection(port1: FeaturePort, port2: FeaturePort, speed: string, no_advertise_speed: string) {
    return this.featurePortConnectionService.create({port1, port2, speed, no_advertise_speed});
  }

  /**
   * Updates the current list of SelectItems to use a filtered list of FeaturePort items.
   * Pre-filters unselectable FeaturePort items out
   *
   * @param {FeaturePort[]} ports
   */
  private refreshSelectOptionsForFeaturePorts(ports: FeaturePort[]) {
    const options: SelectItem[] = [];

    if (!isNullOrUndefined(ports)) {
      ports
        .filter((featurePort: FeaturePort) => {
          let isValid = !isNullOrUndefined(this.source);
          if (isValid) {
            isValid = this.isPortConnectionValidForFeaturePort(this.source, featurePort);
          }
          return isValid;
        })
        .forEach((featurePort: FeaturePort) => options.push({label: featurePort.name, value: featurePort}));
    }

    this.portOptions = this.sortByLabel(options);
  }

  /**
   * Updates the current list of SelectItems to use a unfiltered list of Port items.
   *
   * @param {FeaturePort[]} ports
   */
  private refreshSelectOptionsForPorts(ports: Port[]) {
    const options: SelectItem[] = [];

    if (!isNullOrUndefined(ports)) {
      this.ports
        .filter((port: Port) => {
          let isValid = !isNullOrUndefined(this.source);
          if (isValid) {
            isValid = this.isPortConnectionValidForVirtualPort(this.source, port);
          }
          return isValid;
        })

        .forEach((port: Port) => options.push({label: port.name, value: port}));
    }

    this.portOptions = this.sortByLabel(options);
  }

  private isPortConnectionValidForFeaturePort(port1: FeaturePort, port2: FeaturePort): boolean {
    const featureUnitPort1 = extractDataFromModel<FeaturePort>(port1, 'unit_type');
    const featureUnitPort2 = extractDataFromModel<FeaturePort>(port2, 'unit_type');
    let valid = port1 !== port2;
    valid = valid && port1.id !== port2.id;
    valid = valid && featureUnitPort1.href !== featureUnitPort2.href;
    valid = valid && port1.port_type === port2.port_type;
    valid = valid && (
      (port1.input && port2.output && !this.featurePortService.hasIncomingPortConnection(port1))
      || (port1.output && port2.input && !this.featurePortService.hasIncomingPortConnection(port2))
    );

    return valid;
  }

  private isPortConnectionValidForVirtualPort(port1: FeaturePort, port2: Port): boolean {

    let valid = port1 !== port2;
    valid = valid && port1.port_type === port2.type;
    valid = valid && (
      (port1.input && port2.output)
      || (port1.output && port2.input)
    );

    return valid;
  }

  private sortByLabel(options: SelectItem[]): SelectItem[] {
    return options.sort((o1, o2) => {
      return o1.label < o2.label ? -1 : 1;
    });
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_master@2/app/Resources/Public/src/app/modules/models/feature-port/components/create-port-connection/create-port-connection.component.ts